<template>
  <div class="card-list-view">
    <div class="header-actions">
      <h1>All Cards</h1>
      <div class="action-buttons">
        <router-link to="/cards/create" class="btn btn-primary">Create Card</router-link>
        <router-link to="/cards/import" class="btn btn-secondary">Import Cards</router-link>
      </div>
    </div>

    <div v-if="isLoading" class="loading-message">
      <p>Loading cards...</p>
    </div>
    <div v-if="errorMessage" class="error-message">
      <p>{{ errorMessage }}</p>
    </div>

    <div v-if="!isLoading && cards.length > 0" class="cards-container">
      <div class="summary-stats">
        <span class="total-count">Total cards: {{ totalItems }}</span>
        <span v-if="summaryStats.total_pairs" class="stat-item">| Pairs: {{ summaryStats.total_pairs }}</span>
        <span v-if="summaryStats.cards_not_reviewed !== undefined" class="stat-item">| Not reviewed: {{ summaryStats.cards_not_reviewed }}</span>
        <span v-if="summaryStats.cards_learned !== undefined" class="stat-item">| Learned: {{ summaryStats.cards_learned }}</span>
      </div>
      <table>
        <thead>
          <tr>
            <th @click="sortBy('card_id')" class="sortable">
              ID <span class="sort-indicator">{{ getSortIndicator('card_id') }}</span>
            </th>
            <th @click="sortBy('front')" class="sortable">
              Front <span class="sort-indicator">{{ getSortIndicator('front') }}</span>
            </th>
            <th @click="sortBy('back')" class="sortable">
              Back <span class="sort-indicator">{{ getSortIndicator('back') }}</span>
            </th>
            <th>Tags</th>
            <th>Notes</th>
            <th @click="sortBy('mastery')" class="sortable">
              Mastery <span class="sort-indicator">{{ getSortIndicator('mastery') }}</span>
            </th>
            <th @click="sortBy('is_learning')" class="sortable">
              Status <span class="sort-indicator">{{ getSortIndicator('is_learning') }}</span>
            </th>
            <th @click="sortBy('next_review_date')" class="sortable">
              Next Review <span class="sort-indicator">{{ getSortIndicator('next_review_date') }}</span>
            </th>
            <th @click="sortBy('total_reviews')" class="sortable">
              Reviews <span class="sort-indicator">{{ getSortIndicator('total_reviews') }}</span>
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="card in cards" :key="card.card_id">
            <tr @click="toggleReviews(card)" class="card-row" :class="{ 'expanded': expandedCards[card.card_id] }">
              <td>{{ card.card_id }}</td>
              <td class="text-preview">
                <div class="front-display">
                  <span class="front-word">{{ getFrontWord(card.front) }}</span>
                  <span v-if="hasContext(card.front)" class="front-context" :title="getContext(card.front)">
                    {{ getContextPreview(card.front) }}
                  </span>
                </div>
              </td>
              <td class="text-preview">
                <div class="front-display">
                  <span class="front-word">{{ getFrontWord(card.back) }}</span>
                  <span v-if="hasContext(card.back)" class="front-context" :title="getContext(card.back)">
                    {{ getContextPreview(card.back) }}
                  </span>
                </div>
              </td>
              <td>
                <span v-for="tag in card.tags" :key="tag" class="tag-badge">{{ tag }}</span>
                <span v-if="!card.tags || card.tags.length === 0">-</span>
              </td>
              <td class="notes-preview" :title="card.notes || ''">
                <span v-if="card.notes">{{ card.notes.substring(0, 50) }}{{ card.notes.length > 50 ? '...' : '' }}</span>
                <span v-else>-</span>
              </td>
              <td>
                <span v-if="card.mastery_level" :class="`badge-mastery`" :style="getMasteryStyle(card.mastery_level.score)" :title="card.mastery_level.indicator || ''">
                  {{ card.mastery_level.level }}
                  <span v-if="card.mastery_level.score !== null" class="mastery-score">
                    ({{ card.mastery_level.score.toFixed(2) }})
                  </span>
                </span>
                <span v-else>-</span>
              </td>
              <td>
                <span :class="card.is_learning ? 'badge-learning' : 'badge-review'">
                  {{ card.is_learning ? 'Learning' : 'Review' }}
                </span>
              </td>
              <td>{{ formatDate(card.next_review_date) }}</td>
              <td>
                <button @click.stop="toggleReviews(card)" class="reviews-btn" :class="{ 'has-reviews': card.total_reviews > 0 }">
                  {{ card.total_reviews }} {{ card.total_reviews === 1 ? 'review' : 'reviews' }}
                  <span class="expand-icon">{{ expandedCards[card.card_id] ? '▼' : '▶' }}</span>
                </button>
              </td>
              <td @click.stop>
                <router-link :to="{ name: 'CardEditor', params: { id: card.card_id } }" class="action-link">Edit</router-link>
                <button @click="confirmDelete(card)" class="action-link delete-btn">Delete</button>
              </td>
            </tr>
            <tr v-if="expandedCards[card.card_id]" class="reviews-row">
              <td colspan="10" class="reviews-cell">
                <div v-if="loadingReviews[card.card_id]" class="loading-reviews">
                  Loading reviews...
                </div>
                <div v-else-if="cardReviews[card.card_id] && cardReviews[card.card_id].length > 0" class="reviews-container">
                  <h4>Review History ({{ cardReviews[card.card_id].length }})</h4>
                  <p class="reviews-help-text">Click "Edit" on any review to change its score or comment</p>
                  <table class="reviews-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Score</th>
                        <th>Interval</th>
                        <th>Ease Factor</th>
                        <th>Comment</th>
                        <th>Typed Input</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="review in cardReviews[card.card_id]" :key="review.review_id">
                        <td>{{ formatDateTime(review.review_timestamp) }}</td>
                        <td>
                          <div v-if="isEditingReview(review.review_id)" class="score-edit">
                            <input
                              type="number"
                              min="0"
                              max="1"
                              step="0.01"
                              v-model.number="editingReviews[review.review_id].score"
                              class="score-input"
                            />
                          </div>
                          <span v-else class="score-badge" :class="getScoreClass(review.user_score)">
                            {{ (review.user_score || 0).toFixed(2) }}
                          </span>
                        </td>
                        <td>{{ review.interval_at_review }} days</td>
                        <td>{{ review.ease_factor_at_review ? review.ease_factor_at_review.toFixed(2) : 'N/A' }}</td>
                        <td class="comment-cell">
                          <input
                            v-if="isEditingReview(review.review_id)"
                            type="text"
                            class="comment-input"
                            v-model="editingReviews[review.review_id].comment"
                            placeholder="Add a comment"
                          />
                          <span v-else>{{ review.user_comment_addon || '-' }}</span>
                        </td>
                        <td class="typed-input-cell">{{ review.typed_input || '-' }}</td>
                        <td class="review-actions">
                          <button
                            v-if="isEditingReview(review.review_id)"
                            class="review-action-button save"
                            :disabled="editingReviews[review.review_id].isSaving"
                            @click="saveReviewEdit(card, review)"
                          >
                            {{ editingReviews[review.review_id].isSaving ? 'Saving...' : 'Save' }}
                          </button>
                          <button
                            v-if="isEditingReview(review.review_id)"
                            class="review-action-button cancel"
                            :disabled="editingReviews[review.review_id].isSaving"
                            @click="cancelEditReview(review.review_id)"
                          >
                            Cancel
                          </button>
                          <button
                            v-else
                            class="review-action-button edit"
                            @click="startEditReview(review)"
                            title="Click to edit this review's score and comment"
                          >
                            ✏️ Edit
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="no-reviews">
                  No reviews yet for this card.
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <div class="pagination-controls">
        <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1 || isFetchingPage">
          Previous
        </button>
        <span>Page {{ currentPage }} of {{ totalPages }}</span>
        <button @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages || isFetchingPage">
          Next
        </button>
      </div>
    </div>
    <div v-if="!isLoading && cards.length === 0 && !errorMessage" class="no-data-message">
      <p>No cards found. <router-link to="/cards/create">Create your first card</router-link> or <router-link to="/cards/import">import from CSV</router-link>.</p>
    </div>
  </div>
</template>

<script>
import ApiService from '@/services/ApiService';

export default {
  name: 'CardListView',
  data() {
    return {
      cards: [],
      isLoading: true,
      isFetchingPage: false,
      errorMessage: '',
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      expandedCards: {},
      cardReviews: {},
      loadingReviews: {},
      editingReviews: {},
      sortColumn: null,
      sortDirection: 'asc',
      summaryStats: {},
    };
  },
  methods: {
    async fetchCards(page) {
      if (this.isFetchingPage) return;
      this.isLoading = this.currentPage === 1;
      this.isFetchingPage = true;
      this.errorMessage = '';
      try {
        const response = await ApiService.getAllCards(page);
        if (response.status === 200 && response.data) {
          this.cards = response.data.results || [];
          this.totalPages = Number.isInteger(response.data.total_pages) ? response.data.total_pages : 0;
          this.totalItems = Number.isInteger(response.data.count) ? response.data.count : 0;
          this.currentPage = page;

          // Re-apply sorting if active
          if (this.sortColumn) {
            this.sortBy(this.sortColumn);
            // Toggle direction back since sortBy will toggle it
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
          }
        } else {
          this.errorMessage = "Could not load cards. The server didn't return valid data.";
        }
      } catch (error) {
        console.error(`Error fetching cards (page ${page}):`, error);
        this.errorMessage = 'Failed to load cards. Please check your connection or try again later.';
        this.cards = [];
        this.totalPages = 0;
        this.totalItems = 0;
      } finally {
        this.isLoading = false;
        this.isFetchingPage = false;
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.fetchCards(page);
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    async confirmDelete(card) {
      if (confirm(`Delete card "${card.front.substring(0, 50)}..."? This will also delete the reverse card if it exists.`)) {
        try {
          await ApiService.deleteCard(card.card_id);
          this.fetchCards(this.currentPage);
        } catch (error) {
          console.error('Error deleting card:', error);
          alert('Failed to delete card. Please try again.');
        }
      }
    },
    async toggleReviews(card) {
      const cardId = card.card_id;
      
      // If already expanded, collapse
      if (this.expandedCards[cardId]) {
        this.expandedCards[cardId] = false;
        return;
      }
      
      // Expand and fetch reviews if not already loaded
      this.expandedCards[cardId] = true;
      
      if (!this.cardReviews[cardId] && !this.loadingReviews[cardId]) {
        this.loadingReviews[cardId] = true;
        try {
          const response = await ApiService.getCardDetails(cardId);
          if (response.status === 200 && response.data && response.data.reviews) {
            // Sort reviews by timestamp (newest first)
            const reviews = [...response.data.reviews].sort((a, b) => {
              return new Date(b.review_timestamp) - new Date(a.review_timestamp);
            });
            this.cardReviews[cardId] = reviews;
          } else {
            this.cardReviews[cardId] = [];
          }
        } catch (error) {
          console.error(`Error fetching reviews for card ${cardId}:`, error);
          this.cardReviews[cardId] = [];
        } finally {
          this.loadingReviews[cardId] = false;
        }
      }
    },
    isEditingReview(reviewId) {
      return Boolean(this.editingReviews[reviewId]);
    },
    startEditReview(review) {
      // Vue 3: Direct assignment works with reactive objects
      this.editingReviews[review.review_id] = {
        score: review.user_score || 0,
        comment: review.user_comment_addon || '',
        isSaving: false,
      };
    },
    cancelEditReview(reviewId) {
      // Vue 3: Use delete operator
      delete this.editingReviews[reviewId];
    },
    async saveReviewEdit(card, review) {
      const editState = this.editingReviews[review.review_id];
      if (!editState || editState.isSaving) return;

      const score = Number(editState.score);
      if (Number.isNaN(score) || score < 0 || score > 1) {
        alert('Score must be between 0 and 1.');
        return;
      }

      editState.isSaving = true;
      try {
        const response = await ApiService.updateCardReview(review.review_id, {
          user_score: score,
          user_comment_addon: editState.comment,
        });

        if (response.status === 200 && response.data) {
          const updatedCard = response.data;
          const cardIndex = this.cards.findIndex((existing) => existing.card_id === updatedCard.card_id);
          if (cardIndex !== -1) {
            // Vue 3: Direct assignment works with reactive arrays
            this.cards[cardIndex] = updatedCard;
          }

          if (updatedCard.reviews) {
            this.cardReviews[card.card_id] = [...updatedCard.reviews].sort((a, b) => {
              return new Date(b.review_timestamp) - new Date(a.review_timestamp);
            });
          }
          this.fetchSummaryStats();
        }

        // Vue 3: Use delete operator
        delete this.editingReviews[review.review_id];
      } catch (error) {
        console.error(`Error updating review ${review.review_id}:`, error);
        alert('Failed to update review. Please try again.');
        editState.isSaving = false;
      }
    },
    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return date.toLocaleDateString(undefined, options);
    },
    getScoreClass(score) {
      if (score >= 0.9) return 'score-excellent';
      if (score >= 0.7) return 'score-good';
      if (score >= 0.5) return 'score-fair';
      return 'score-poor';
    },
    sortBy(column) {
      if (this.sortColumn === column) {
        // Toggle direction if clicking same column
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // New column, default to ascending
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }

      // Sort the cards array
      this.cards.sort((a, b) => {
        let valueA, valueB;

        switch (column) {
          case 'card_id':
            valueA = a.card_id;
            valueB = b.card_id;
            break;
          case 'front':
            valueA = (a.front || '').toLowerCase();
            valueB = (b.front || '').toLowerCase();
            break;
          case 'back':
            valueA = (a.back || '').toLowerCase();
            valueB = (b.back || '').toLowerCase();
            break;
          case 'mastery':
            valueA = a.mastery_level?.score ?? -1;
            valueB = b.mastery_level?.score ?? -1;
            break;
          case 'is_learning':
            valueA = a.is_learning ? 0 : 1; // Learning first
            valueB = b.is_learning ? 0 : 1;
            break;
          case 'next_review_date':
            valueA = a.next_review_date ? new Date(a.next_review_date).getTime() : Infinity;
            valueB = b.next_review_date ? new Date(b.next_review_date).getTime() : Infinity;
            break;
          case 'total_reviews':
            valueA = a.total_reviews ?? 0;
            valueB = b.total_reviews ?? 0;
            break;
          default:
            return 0;
        }

        if (valueA < valueB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    },
    getSortIndicator(column) {
      if (this.sortColumn !== column) return '';
      return this.sortDirection === 'asc' ? '▲' : '▼';
    },
    getMasteryStyle(score) {
      if (score === null || score === undefined) {
        return {
          backgroundColor: '#6c757d',
          color: 'white'
        };
      }

      // Color gradient from red (0) -> yellow (0.5) -> green (1.0)
      let r, g, b;

      if (score < 0.5) {
        // Red to Yellow (0.0 to 0.5)
        const normalized = score * 2; // 0 to 1
        r = 220; // Keep red high
        g = Math.round(100 + (155 * normalized)); // 100 to 255
        b = 50;
      } else {
        // Yellow to Green (0.5 to 1.0)
        const normalized = (score - 0.5) * 2; // 0 to 1
        r = Math.round(220 - (180 * normalized)); // 220 to 40
        g = 200 + Math.round(55 * normalized); // 200 to 255
        b = 50 + Math.round(50 * normalized); // 50 to 100
      }

      // Determine text color based on brightness
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      const textColor = brightness > 155 ? '#000' : '#fff';

      return {
        backgroundColor: `rgb(${r}, ${g}, ${b})`,
        color: textColor
      };
    },
    async fetchSummaryStats() {
      try {
        const response = await ApiService.getCardStatistics();
        if (response.status === 200 && response.data) {
          this.summaryStats = response.data;
        }
      } catch (error) {
        console.warn('Failed to fetch summary stats:', error);
      }
    },
    getFrontWord(text) {
      if (!text) return '';
      // Extract word (first line before "\n\nContext:" or just first line)
      const contextIndex = text.indexOf('\n\nContext:');
      if (contextIndex !== -1) {
        return text.substring(0, contextIndex).trim();
      }
      // If no context marker, return first line
      return text.split('\n')[0].trim();
    },
    hasContext(text) {
      if (!text) return false;
      return text.includes('\n\nContext:');
    },
    getContext(text) {
      if (!text) return '';
      const contextIndex = text.indexOf('\n\nContext:');
      if (contextIndex !== -1) {
        return text.substring(contextIndex + 2).trim(); // +2 to skip "\n\n"
      }
      return '';
    },
    getContextPreview(text) {
      const context = this.getContext(text);
      if (!context) return '';
      // Remove "Context: " prefix and show preview
      const contextText = context.replace(/^Context:\s*/i, '');
      // Show first 30 chars with ellipsis
      if (contextText.length > 30) {
        return ` • ${contextText.substring(0, 30)}...`;
      }
      return ` • ${contextText}`;
    }
  },
  mounted() {
    this.fetchCards(this.currentPage);
    this.fetchSummaryStats();
  },
};
</script>

<style scoped>
.card-list-view {
  padding: 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  max-width: 1400px;
  margin: 0 auto;
}

.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn {
  padding: 10px 20px;
  border-radius: 4px;
  text-decoration: none;
  font-weight: bold;
  transition: background-color 0.2s;
}

.btn-primary {
  background-color: var(--button-primary);
  color: white;
}

.btn-primary:hover {
  background-color: var(--button-primary-hover);
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #545b62;
}

.loading-message, .error-message, .no-data-message {
  text-align: center;
  padding: 20px;
  background-color: var(--bg-secondary);
  border-radius: 8px;
  margin: 20px auto;
  color: var(--text-primary);
}

.error-message {
  color: var(--error-color);
  background-color: var(--error-bg);
}

.summary-stats {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-bottom: 10px;
  font-size: 0.9em;
  color: var(--text-secondary);
  flex-wrap: wrap;
}

.total-count {
  font-weight: 500;
}

.stat-item {
  color: var(--text-secondary);
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 15px;
  box-shadow: 0 1px 3px var(--shadow);
}

thead {
  background-color: var(--button-primary);
  color: white;
}

th, td {
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  text-align: left;
  font-size: 0.9em;
  color: var(--text-primary);
}

th.sortable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

th.sortable:hover {
  background-color: var(--button-primary-hover);
}

.sort-indicator {
  margin-left: 5px;
  font-size: 0.8em;
  opacity: 0.8;
}

.text-preview {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.front-display {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.front-word {
  font-weight: 500;
  color: var(--text-primary);
}

.front-context {
  font-size: 0.85em;
  color: var(--text-secondary);
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notes-preview {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85em;
  color: var(--text-secondary);
}

tr:nth-child(even) {
  background-color: var(--bg-secondary);
}

tr:hover {
  background-color: var(--bg-tertiary);
}

.tag-badge {
  display: inline-block;
  background-color: var(--bg-tertiary);
  color: var(--text-primary);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.8em;
  margin-right: 5px;
}

.badge-learning {
  background-color: #ffc107;
  color: #000;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 0.8em;
  font-weight: bold;
}

.badge-review {
  background-color: #28a745;
  color: white;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 0.8em;
  font-weight: bold;
}

.badge-mastery {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.85em;
  font-weight: bold;
  display: inline-block;
  transition: all 0.2s;
}

.mastery-score {
  font-size: 0.85em;
  opacity: 0.9;
  margin-left: 4px;
}

.pagination-controls {
  margin-top: 20px;
  text-align: center;
}

.pagination-controls button {
  padding: 8px 15px;
  margin: 0 5px;
  border: 1px solid #007bff;
  background-color: #fff;
  color: #007bff;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
}

.pagination-controls button:hover:not(:disabled) {
  background-color: #007bff;
  color: white;
}

.pagination-controls button:disabled {
  border-color: #ccc;
  color: #ccc;
  cursor: not-allowed;
}

.pagination-controls span {
  margin: 0 10px;
  vertical-align: middle;
}

.action-link {
  color: #007bff;
  text-decoration: none;
  margin-right: 10px;
  cursor: pointer;
}

.action-link:hover {
  text-decoration: underline;
}

.delete-btn {
  color: #dc3545;
  background: none;
  border: none;
  padding: 0;
  font-size: inherit;
}

.delete-btn:hover {
  color: #c82333;
}

.card-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.card-row:hover {
  background-color: var(--bg-tertiary);
}

.card-row.expanded {
  background-color: var(--bg-tertiary);
}

.reviews-btn {
  background: none;
  border: none;
  color: var(--button-primary);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s;
}

.reviews-btn:hover {
  background-color: var(--bg-tertiary);
}

.reviews-btn.has-reviews {
  font-weight: bold;
}

.expand-icon {
  font-size: 0.8em;
  transition: transform 0.2s;
}

.reviews-row {
  background-color: var(--bg-secondary);
}

.reviews-cell {
  padding: 20px !important;
  background-color: var(--bg-secondary);
}

.loading-reviews {
  text-align: center;
  padding: 20px;
  color: var(--text-secondary);
}

.reviews-container {
  max-width: 100%;
  overflow-x: auto;
  position: relative;
}

/* Ensure Actions column is always visible */
.reviews-table th:last-child,
.reviews-table td:last-child {
  position: sticky;
  right: 0;
  background-color: var(--card-bg);
  z-index: 10;
  min-width: 120px;
  white-space: nowrap;
}

.reviews-table thead th:last-child {
  background-color: #6c757d;
  z-index: 11;
}

.reviews-container h4 {
  margin: 0 0 15px 0;
  color: var(--text-primary);
  font-size: 1.1em;
}

.reviews-table {
  width: 100%;
  border-collapse: collapse;
  background-color: var(--card-bg);
  box-shadow: 0 1px 3px var(--shadow);
}

.reviews-table thead {
  background-color: #6c757d;
  color: white;
}

.reviews-table th,
.reviews-table td {
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  text-align: left;
  font-size: 0.85em;
  color: var(--text-primary);
}

/* Make Actions column sticky and always visible */
.reviews-table th:last-child,
.reviews-table td:last-child {
  position: sticky;
  right: 0;
  background-color: var(--card-bg);
  z-index: 10;
  min-width: 150px;
  white-space: nowrap;
  text-align: center;
}

.reviews-table thead th:last-child {
  background-color: #6c757d;
  z-index: 11;
}

.reviews-table tbody tr:nth-child(even) td:last-child {
  background-color: var(--bg-secondary);
}

.reviews-table tbody tr:hover td:last-child {
  background-color: var(--bg-tertiary);
}

.reviews-table tbody tr:nth-child(even) {
  background-color: var(--bg-secondary);
}

.reviews-table tbody tr:hover {
  background-color: var(--bg-tertiary);
}

.comment-cell,
.typed-input-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.score-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: bold;
  font-size: 0.9em;
}

.score-edit {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.score-input {
  width: 70px;
  padding: 4px 6px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 0.85em;
}

.score-suffix {
  font-size: 0.85em;
  color: var(--text-secondary);
}

.comment-input {
  width: 100%;
  min-width: 160px;
  padding: 4px 6px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 0.85em;
}

.review-actions {
  white-space: nowrap;
  text-align: center;
  padding: 8px !important;
}

.review-action-button {
  border: 1px solid;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  padding: 6px 12px;
  margin: 0 4px;
  transition: all 0.2s;
  font-weight: 500;
}

.review-action-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.review-action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.review-action-button.edit {
  color: #007bff;
  background-color: rgba(0, 123, 255, 0.1);
  border-color: #007bff;
}

.review-action-button.edit:hover:not(:disabled) {
  background-color: rgba(0, 123, 255, 0.2);
}

.review-action-button.save {
  color: #28a745;
  background-color: rgba(40, 167, 69, 0.1);
  border-color: #28a745;
  font-weight: 600;
}

.review-action-button.save:hover:not(:disabled) {
  background-color: rgba(40, 167, 69, 0.2);
}

.review-action-button.cancel {
  color: #6c757d;
  background-color: rgba(108, 117, 125, 0.1);
  border-color: #6c757d;
}

.review-action-button.cancel:hover:not(:disabled) {
  background-color: rgba(108, 117, 125, 0.2);
}

.reviews-help-text {
  font-size: 0.9em;
  color: var(--text-secondary);
  margin: 0 0 15px 0;
  font-style: italic;
}

.score-excellent {
  background-color: #28a745;
  color: white;
}

.score-good {
  background-color: #17a2b8;
  color: white;
}

.score-fair {
  background-color: #ffc107;
  color: #000;
}

.score-poor {
  background-color: #dc3545;
  color: white;
}

.no-reviews {
  text-align: center;
  padding: 20px;
  color: var(--text-secondary);
  font-style: italic;
}
</style>
